/**
 * @page /portal/zeitkonto
 * @description Zeitkonto-Übersicht für Mitarbeiter (read-only).
 *   Live-Status (heute), Monats-KPIs, Tagesübersicht, 6-Monats-Trend.
 * @api GET /api/portal/zeitkonto/:monat, GET /api/portal/zeitkonto/historie
 * @api GET /api/stempel/mein-status
 * @roles mitarbeiter, admin, manager
 * @i18n portal.zeitkonto
 * @since v2.207.0
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Clock, ChevronLeft, ChevronRight, Coffee, Umbrella, Activity, Timer, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

function fmtMin(min) {
  if (min == null) return '–';
  const h = Math.floor(Math.abs(min) / 60);
  const m = Math.round(Math.abs(min) % 60);
  const sign = min < 0 ? '-' : '';
  return `${sign}${h}:${String(m).padStart(2, '0')}`;
}

function fmtHours(h) {
  if (h == null) return '–';
  return `${Math.round(h * 10) / 10}h`;
}

function fmtTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function fmtDateShort(ds) {
  if (!ds) return '';
  const [, m, d] = ds.split('-');
  const WOCHENTAGE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  const dt = new Date(ds + 'T00:00:00');
  return `${WOCHENTAGE[dt.getDay()]} ${d}.${m}.`;
}

function StatCard({ label, value, sub, icon: Icon, iconColor }) {
  return (
    <div className="bg-surface-card border border-line rounded-card p-4">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className={`w-4 h-4 ${iconColor || 'text-[var(--text-secondary)]'}`} />}
        <span className="text-[11px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-xl font-bold font-mono text-content">{value}</div>
      {sub && <div className="text-xs text-[var(--text-tertiary)] mt-1">{sub}</div>}
    </div>
  );
}

const STEMPEL_LABEL_KEYS = {
  arbeit_start: 'portal.zeitkonto.stampStart',
  kurze_pause: 'portal.zeitkonto.shortBreak',
  mittagspause: 'portal.zeitkonto.lunchBreak',
  pause_ende: 'portal.zeitkonto.stampReturn',
  mittag_ende: 'portal.zeitkonto.stampReturn',
  arbeitsende: 'portal.zeitkonto.stampEnd',
};

export default function PortalZeitkontoPage({ embedded = false }) {
  const { t } = useLanguage();
  const today = new Date();
  const [monat, setMonat] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);
  const [zkData, setZkData] = useState(null);
  const [historie, setHistorie] = useState([]);
  const [liveStatus, setLiveStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState(null);
  const [mehrUeber, setMehrUeber] = useState(null); // #890
  const [zeigeBilanz, setZeigeBilanz] = useState(false); // #1782, Default AUS
  const [lightModus, setLightModus] = useState(false); // #1808, Default AUS

  // Load zeitkonto data
  const loadZeitkonto = useCallback(async () => {
    setLoading(true);
    try {
      const [zkRes, hRes] = await Promise.all([
        fetch(`${API}/api/portal/zeitkonto/${monat}`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/portal/zeitkonto/historie`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setZkData(zkRes);
      setHistorie(Array.isArray(hRes) ? hRes : []);
      setMehrUeber(zkRes?.mehr_ueber || null);
      setZeigeBilanz(zkRes?.zeige_bilanz_zeitguthaben === true);
      setLightModus(zkRes?.app_light_modus === true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [monat]);

  useEffect(() => { loadZeitkonto(); }, [loadZeitkonto]);

  // Live status polling (30s)
  const loadLive = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/stempel/mein-status`, { credentials: 'include' });
      if (res.ok) setLiveStatus(await res.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadLive();
    const iv = setInterval(loadLive, 30000);
    return () => clearInterval(iv);
  }, [loadLive]);

  // Monat navigation
  const prevMonat = () => {
    const [y, m] = monat.split('-').map(Number);
    setMonat(m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`);
  };
  const nextMonat = () => {
    const [y, m] = monat.split('-').map(Number);
    setMonat(m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`);
  };

  // Check if current month
  const isCurrentMonth = monat === `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

  // #1777 Punkt 4: „genommen" (Roh-ZA aus den Tagen) ist NICHT „abgezogen" (bewerteter
  // Konto-Abzug). Beide Werte getrennt ausweisen, damit das Label nicht mehr lügt.
  const zaGenommenMin = useMemo(
    () => (zkData?.tage || []).reduce((s, tg) => s + (Number(tg.za_min) || 0), 0),
    [zkData]
  );
  const zaAbzugMin = zkData?.kontoauszug
    ? Math.round(Math.abs(zkData.kontoauszug.za_abzug_summe) * 60)
    : Math.round((zkData?.za_genommen || 0) * 60);

  // Chart data
  const chartData = useMemo(() => {
    return historie.slice(-6).map(h => {
      const m = h.monat?.slice(0, 7) || '';
      const [, mm] = m.split('-');
      return {
        name: mm ? `${parseInt(mm)}/${m.slice(2, 4)}` : '',
        soll: Number(h.soll_stunden || 0),
        ist: Number(h.ist_stunden || 0),
        ueberstunden: Number(h.ueberstunden || 0),
      };
    });
  }, [historie]);

  if (loading) {
    return (
      <div className={embedded ? '' : 'p-page'}>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-inset rounded w-1/3" />
          <div className="h-24 bg-surface-inset rounded-card" />
          <div className="grid grid-cols-4 gap-3">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-surface-inset rounded-card" />)}
          </div>
          <div className="h-64 bg-surface-inset rounded-card" />
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-page'}>
      {/* #1593: kein in-page PageHeader mehr — embedded=false ist jetzt nur die Mobile-Route,
          deren PortalMobileLayout bereits einen Sticky-Header "Arbeitszeiten" zeigt (sonst Doppel-Titel). */}

      {/* Heute — Stempel-Verlauf + KPIs (#1593, portiert aus MobileZeiten.renderHeute) */}
      {liveStatus && liveStatus.app_enabled !== false && (() => {
        const stamps = liveStatus.heutige_stempel || [];
        const istMin = liveStatus.arbeitszeit_min || 0;
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        // #1808: im Light-Modus ist `tage` leer — der Server liefert den heutigen Tag
        // separat, damit SOLL/Rest hier nicht still auf „–" fallen.
        const heuteTag = zkData?.tage?.find(d => d.datum === todayStr) || zkData?.heute_tag || null;
        const sollMin = heuteTag && heuteTag.soll_min != null ? Number(heuteTag.soll_min) : null;
        const isFreeDay = !!heuteTag && (heuteTag.ist_wochenende || heuteTag.ist_feiertag
          || (Number(heuteTag.soll_min || 0) === 0 && !heuteTag.abwesenheit));
        const restMin = (!isFreeDay && sollMin != null) ? Math.max(0, sollMin - istMin) : null;
        const statusLabel = liveStatus.status === 'eingestempelt' ? t('portal.zeitkonto.statusActive')
          : liveStatus.status === 'in_pause' ? t('portal.zeitkonto.statusPause')
          : t('portal.zeitkonto.statusOff');
        return (
          <div className="mb-section space-y-3">
            {/* Timeline */}
            <div className="bg-surface-card border border-line rounded-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-2.5 h-2.5 rounded-full ${liveStatus.status === 'eingestempelt' ? 'bg-emerald-400 animate-pulse' : liveStatus.status === 'in_pause' ? 'bg-yellow-400 animate-pulse' : 'bg-[var(--text-tertiary,#6B7280)]'}`} />
                <h3 className="text-sm font-bold text-content uppercase tracking-wider">{t('portal.zeitkonto.today')}</h3>
              </div>
              {stamps.length === 0 ? (
                <p className="text-sm text-[var(--text-tertiary)] py-2">{t('portal.zeitkonto.notClockedIn')}</p>
              ) : (
                <div className="relative pl-6">
                  <div className="absolute left-[9px] top-1 bottom-1 w-[2px] bg-[var(--border-card,#E5E7EB)]" />
                  {stamps.map((s, i) => {
                    const isLast = i === stamps.length - 1;
                    const isStart = s.typ === 'arbeit_start';
                    const isPause = ['kurze_pause', 'mittagspause'].includes(s.typ);
                    const dotColor = isStart ? 'bg-[#338edc]' : isPause ? 'bg-amber-400' : 'bg-[#338edc]';
                    // #1777 Punkt 1: erster Stempel = Check-in (Realzeit); die gewertete
                    //   Arbeitszeit startet ggf. später — Zusatzzeile nur dann.
                    const isCheckIn = isStart && i === 0;
                    return (
                      <div key={s.id ?? i} className="relative flex items-start gap-3 pb-4 last:pb-0">
                        <div className={`absolute -left-6 top-[3px] w-[10px] h-[10px] rounded-full ${dotColor} ring-2 ring-white z-10`} />
                        <div className="flex-1">
                          <div className="flex items-baseline justify-between">
                            <span className={`text-sm ${isLast ? 'font-semibold text-content' : 'text-[var(--text-secondary)]'}`}>
                              {isCheckIn
                                ? t('portal.zeitkonto.checkIn', 'Check-in')
                                : (STEMPEL_LABEL_KEYS[s.typ] ? t(STEMPEL_LABEL_KEYS[s.typ]) : s.typ)}
                            </span>
                            <span className="text-sm font-mono text-content">{fmtTime(s.zeitpunkt)}</span>
                          </div>
                          {isCheckIn && liveStatus.arbeitszeit_ab && (
                            <div className="flex items-baseline justify-between text-[11px] text-[var(--text-tertiary)] mt-0.5">
                              <span>{t('portal.zeitkonto.arbeitszeitAb', 'Arbeitszeit ab')}</span>
                              <span className="font-mono">{fmtTime(liveStatus.arbeitszeit_ab)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* KPI cards (individuelles Tages-Soll #1589 + Frei) */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: t('portal.zeitkonto.soll'), value: isFreeDay ? t('portal.zeitkonto.frei') : (sollMin != null ? fmtMin(sollMin) : '–') },
                { label: t('portal.zeitkonto.ist'), value: fmtMin(istMin) },
                { label: t('portal.zeitkonto.rest'), value: isFreeDay ? '–' : (restMin != null ? fmtMin(restMin) : '–') },
              ].map((kpi, i) => (
                <div key={i} className="bg-surface-card border border-line rounded-card p-3 text-center">
                  <p className="text-[11px] text-[var(--text-tertiary)] mb-1">{kpi.label}</p>
                  <p className="text-lg font-bold font-mono text-content">{kpi.value}</p>
                </div>
              ))}
            </div>

            {/* Status badge */}
            <div className="bg-surface-card border border-line rounded-card p-4 flex items-center justify-between">
              <span className="text-sm text-[var(--text-secondary)]">{t('common.status')}</span>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                liveStatus.status === 'eingestempelt' ? 'bg-[#338edc]/10 text-[#338edc]' :
                liveStatus.status === 'in_pause' ? 'bg-amber-50 text-amber-700' :
                'bg-[var(--bg-inset,#F3F4F6)] text-[var(--text-tertiary)]'
              }`}>{statusLabel}</span>
            </div>
          </div>
        );
      })()}

      {/* Monats-Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button onClick={prevMonat} className="p-1 rounded hover:bg-surface-inset"><ChevronLeft className="w-4 h-4 text-[var(--text-secondary)]" /></button>
          <span className="text-sm font-semibold text-content min-w-[120px] text-center">
            {t(`common.months.${parseInt(monat.split('-')[1]) - 1}`)} {monat.split('-')[0]}
          </span>
          <button onClick={nextMonat} className="p-1 rounded hover:bg-surface-inset"><ChevronRight className="w-4 h-4 text-[var(--text-secondary)]" /></button>
        </div>
        {isCurrentMonth && <span className="text-[10px] text-[var(--text-tertiary)] bg-surface-inset px-2 py-1 rounded">{t('portal.zeitkonto.monthRunning')}</span>}
      </div>

      {/* KPI Cards */}
      {/* #1808: im Light-Modus bleiben nur SOLL/IST — Grid schaltet auf 2 Spalten,
          damit keine Luecke entsteht (Tailwind braucht statische Klassennamen). */}
      {zkData && (
        <div className={`grid grid-cols-2 ${lightModus ? 'lg:grid-cols-2' : 'lg:grid-cols-4'} gap-3 mb-section`}>
          {/* #1864 F5: Kopf-Werte aus persistierten Tageszeilen bis gestern (Server `anzeige`), Fallback zeitkonto */}
          <StatCard icon={Clock} iconColor="text-[#338edc]" label={t('portal.zeitkonto.soll')} value={fmtHours(zkData.anzeige ? zkData.anzeige.soll_min / 60 : zkData.soll_stunden)}
            sub={zkData.anzeige?.stichtag ? t('portal.zeitkonto.standBis', { defaultValue: 'Stand bis {{datum}}', datum: `${zkData.anzeige.stichtag.slice(8, 10)}.${zkData.anzeige.stichtag.slice(5, 7)}.${zkData.anzeige.stichtag.slice(0, 4)}` }) : undefined} />
          <StatCard icon={Activity} iconColor="text-[#22D3EE]" label={t('portal.zeitkonto.ist')} value={fmtHours(zkData.anzeige ? zkData.anzeige.ist_min / 60 : zkData.ist_stunden)} />
          {!lightModus && (<>
          <StatCard icon={TrendingUp} iconColor="text-[#10B981]" label={t('portal.zeitkonto.delta')} value={fmtHours(zkData.anzeige?.delta_min != null ? zkData.anzeige.delta_min / 60 : zkData.delta_stunden)} sub={zkData.delta_stunden > 0 ? `+${fmtHours(zkData.ueberstunden)} ${t('portal.zeitkonto.overtime')}` : zkData.minusstunden > 0 ? `${fmtHours(zkData.minusstunden)} ${t('portal.zeitkonto.deficit')}` : undefined} />
          <StatCard icon={Timer} iconColor="text-[#F59E0B]" label={t('portal.zeitkonto.offenerBestand', 'Offener Bestand')} value={fmtHours(zkData.za_saldo)}
            sub={
              mehrUeber && mehrUeber.geplant_abbau_min > 0
                ? `${t('portal.zeitkonto.davon', 'davon')} ${(mehrUeber.geplant_abbau_min / 60).toFixed(1).replace('.', ',')}h ${t('portal.zeitkonto.alsZaGeplant', 'als ZA geplant')} → ${(mehrUeber.verbleibend_nach_plan_min / 60).toFixed(1).replace('.', ',')}h ${t('portal.zeitkonto.nachAbbau', 'nach Abbau')}`
                : (zaAbzugMin > 0
                    ? (zaGenommenMin > 0 && zaGenommenMin !== zaAbzugMin
                        ? `${fmtMin(zaGenommenMin)} ${t('portal.zeitkonto.zaTaken', 'genommen')} · ${fmtMin(zaAbzugMin)} ${t('portal.zeitkonto.zaDeducted', 'vom ZA-Konto abgezogen')}`
                        : `${fmtMin(zaAbzugMin)} ${t('portal.zeitkonto.zaDeducted', 'vom ZA-Konto abgezogen')}`)
                    : undefined)
            } />
          </>)}
        </div>
      )}

      {/* #1776 Kontoauszug ZA-Konto — Beginn + Veränderung − Abzug = Ende */}
      {zkData?.kontoauszug && (
        <div className="bg-surface-card border border-line rounded-card p-4 mb-section">
          <h3 className="text-sm font-bold text-content uppercase tracking-wider mb-3">{t('portal.zeitkonto.kontoauszugTitle', 'Zeitausgleichs-Konto')}</h3>
          <div className="space-y-1 text-sm max-w-md">
            <div className="flex items-center justify-between">
              <span className="text-[var(--text-secondary)]">{t('portal.zeitkonto.saldoBeginn', 'Saldo Monatsbeginn')}</span>
              <span className="font-mono text-content">{fmtMin(Math.round(zkData.kontoauszug.za_saldo_beginn * 60))}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[var(--text-secondary)]">+ {t('portal.zeitkonto.veraenderungArbeitszeit', 'Veränderung durch Arbeitszeit')}</span>
              <span className="font-mono text-content">{fmtMin(Math.round(zkData.kontoauszug.veraenderung_arbeitszeit * 60))}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[var(--text-secondary)]">− {t('portal.zeitkonto.abzugZeitausgleich', 'Abzug durch Zeitausgleich')}</span>
              <span className="font-mono text-content">{fmtMin(-Math.round(Math.abs(zkData.kontoauszug.za_abzug_summe) * 60))}</span>
            </div>
            {zkData.kontoauszug.ausgezahlt > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-[var(--text-secondary)]">− {t('portal.zeitkonto.ausgezahlt', 'Ausgezahlt')}</span>
                <span className="font-mono text-content">{fmtMin(-Math.round(zkData.kontoauszug.ausgezahlt * 60))}</span>
              </div>
            )}
            {zkData.kontoauszug.korrekturen !== 0 && (
              <div className="flex items-center justify-between">
                <span className="text-[var(--text-secondary)]">{t('portal.zeitkonto.korrekturen', 'Korrekturen')}</span>
                <span className="font-mono text-content">{fmtMin(Math.round(zkData.kontoauszug.korrekturen * 60))}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-1 mt-1 border-t border-line">
              <span className="font-semibold text-content">= {t('portal.zeitkonto.saldoEnde', 'Saldo Monatsende')}</span>
              <span className="text-base font-bold font-mono text-content">{fmtMin(Math.round(zkData.kontoauszug.za_saldo_ende * 60))}</span>
            </div>
          </div>
          <div className="text-[11px] text-[var(--text-tertiary)] mt-2">
            {t('portal.zeitkonto.kontoauszugHinweis', 'Der Saldo Monatsende ist der Saldo Monatsbeginn des Folgemonats.')}
          </div>
        </div>
      )}

      {/* #890: Mehr/Überstunden + Grace */}
      {!lightModus && mehrUeber && (mehrUeber.mehrstunden.gesamt_min > 0 || mehrUeber.ueberstunden.gesamt_min > 0) && (
        <div className="bg-surface-card border border-line rounded-card p-4 mb-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-content uppercase tracking-wider">{t('portal.zeitkonto.mehrUeberTitle', 'Mehr-/Überstunden')}</h3>
            <span className="text-[10px] text-[var(--text-tertiary)]" title={t('portal.zeitkonto.tooltipFaktoren', 'Mehrstunden-Faktor (1,25) gilt nach Grace-Frist. Überstunden-Faktor (1,5) gilt immer.')}>
              {t('portal.zeitkonto.grace', 'Grace')}: {mehrUeber.faktoren.grace_monate} {t('portal.zeitkonto.monate', 'Monate')}
            </span>
          </div>
          <div className="text-[11px] text-[var(--text-tertiary)] mb-3 leading-relaxed border-l-2 border-line pl-3">
            {t('portal.zeitkonto.legendeMehrstunden', 'Mehrstunden')}: {t('portal.zeitkonto.legendeMehrstundenText', 'Plus-Zeit bis Tages-Vollzeit (8h). Bei Vollzeit nur an Tagen mit reduziertem Soll.')}<br/>
            {t('portal.zeitkonto.legendeUeberstunden', 'Überstunden')}: {t('portal.zeitkonto.legendeUeberstundenText', 'Plus-Zeit über Tages-Vollzeit hinaus.')}<br/>
            {t('portal.zeitkonto.legendeGrace', 'Grace')}: {t('portal.zeitkonto.legendeGraceText', '3-Monats-Schonfrist — Mehrstunden 1:1 abbaubar (Faktor 1,0). Danach 1,25.')}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-surface-inset rounded-lg px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]" title={t('portal.zeitkonto.tooltipMehrstunden', 'Plus-Zeit bis Tages-Vollzeit (8h)')}>{t('portal.zeitkonto.mehrstunden', 'Mehrstunden')}</div>
              <div className="text-lg font-bold font-mono text-content">{fmtMin(mehrUeber.mehrstunden.gesamt_min)}</div>
              <div className="text-[10px] text-emerald-500" title={t('portal.zeitkonto.tooltipImGrace', 'Innerhalb 3-Monats-Schonfrist — Faktor 1,0')}>
                {fmtMin(mehrUeber.mehrstunden.grace_min)} {t('portal.zeitkonto.imGrace', 'im Grace')} (1,0)
              </div>
              {mehrUeber.mehrstunden.nach_grace_min > 0 && (
                <div className="text-[10px] text-[var(--text-muted)]" title={t('portal.zeitkonto.tooltipNachGrace', 'Nach Grace — Faktor 1,25 beim Abbau')}>
                  {fmtMin(mehrUeber.mehrstunden.nach_grace_min)} {t('portal.zeitkonto.nachGrace', 'nach Grace')} ({String(mehrUeber.faktoren.mehrstunden).replace('.', ',')})
                </div>
              )}
            </div>
            <div className="bg-surface-inset rounded-lg px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]" title={t('portal.zeitkonto.tooltipUeberstunden', 'Plus-Zeit über Tages-Vollzeit hinaus')}>{t('portal.zeitkonto.ueberstunden', 'Überstunden')}</div>
              <div className="text-lg font-bold font-mono text-content">{fmtMin(mehrUeber.ueberstunden.gesamt_min)}</div>
              <div className="text-[10px] text-[var(--text-muted)]" title={t('portal.zeitkonto.tooltipImmer', 'Überstunden haben keine Schonfrist — Faktor 1,5 immer')}>
                {t('portal.zeitkonto.faktor', 'Faktor')} {String(mehrUeber.faktoren.ueberstunden).replace('.', ',')}
              </div>
            </div>
            <div className="bg-surface-inset rounded-lg px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">{t('portal.zeitkonto.gesamtOffen', 'Gesamt offen')}</div>
              <div className="text-lg font-bold font-mono text-content">{fmtMin(mehrUeber.mehrstunden.gesamt_min + mehrUeber.ueberstunden.gesamt_min)}</div>
              <div className="text-[10px] text-[var(--text-muted)]">
                {mehrUeber.mehrstunden.log.length + mehrUeber.ueberstunden.log.length} {t('portal.zeitkonto.eintraege', 'Einträge')}
              </div>
            </div>
          </div>
          {/* #1567 Phase D: Bilanz Gesamt-Zeitguthaben (brutto = za_genommen + ausgezahlt + offen)
              #1782: in der MA-Ansicht per system_config portal_zeige_bilanz_zeitguthaben ausgeblendet
              (Default AUS) — Block bleibt erhalten und ist durch Umlegen des Flags reaktivierbar. */}
          {zeigeBilanz && (
          <div className="mt-4 pt-3 border-t border-line">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-2">{t('portal.zeitkonto.bilanzTitle', 'Bilanz Zeitguthaben')}</div>
            <div className="space-y-1 text-sm max-w-md">
              <div className="flex items-center justify-between">
                <span className="text-[var(--text-secondary)]">{t('portal.zeitkonto.bilanzGeleistet', 'Geleistet (brutto)')}</span>
                <span className="font-bold font-mono text-content">{((mehrUeber.brutto_min || 0) / 60).toFixed(1).replace('.', ',')} h</span>
              </div>
              {mehrUeber.za_genommen_min > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-[var(--text-tertiary)]" title={t('portal.zeitkonto.bilanzZaAbzugTooltip', 'Bewerteter Abzug vom ZA-Konto — kann kleiner sein als die genommene ZA-Zeit (Faktor 1,25 / 1,5).')}>− {t('portal.zeitkonto.bilanzZaAbzug', 'Vom ZA-Konto abgezogen')}</span>
                  <span className="font-mono text-[var(--text-secondary)]">{(mehrUeber.za_genommen_min / 60).toFixed(1).replace('.', ',')} h</span>
                </div>
              )}
              {mehrUeber.ausgezahlt_min > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-[var(--text-tertiary)]" title={t('portal.zeitkonto.ausgezahltTooltip', 'Bereits ausbezahlte Überstunden — 1:1 aus dem Bestand abgezogen.')}>− {t('portal.zeitkonto.ausgezahlt', 'Ausgezahlt')}</span>
                  <span className="font-mono text-[var(--text-secondary)]">{(mehrUeber.ausgezahlt_min / 60).toFixed(1).replace('.', ',')} h</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-1 mt-1 border-t border-line">
                <span className="font-semibold text-content">= {t('portal.zeitkonto.offenerBestand', 'Offener Bestand')}</span>
                <span className="text-base font-bold font-mono text-content">{((mehrUeber.offen_gesamt_min || 0) / 60).toFixed(1).replace('.', ',')} h</span>
              </div>
              {/* #1603: geplanter ZA-Abbau (genehmigte Zukunfts-ZA) — nur wenn vorhanden */}
              {mehrUeber.geplant_abbau_min > 0 && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-tertiary)]" title={t('portal.zeitkonto.geplantAbbauTooltip', 'Bereits genehmigte, künftig geplante Zeitausgleichs-Tage — werden erst beim Antritt vom Bestand abgezogen.')}>− {t('portal.zeitkonto.geplantAbbau', 'Geplanter ZA-Abbau (genehmigt)')}</span>
                    <span className="font-mono text-[var(--text-secondary)]">{(mehrUeber.geplant_abbau_min / 60).toFixed(1).replace('.', ',')} h</span>
                  </div>
                  <div className="flex items-center justify-between pt-1 mt-1 border-t border-line">
                    <span className="font-semibold text-content">= {t('portal.zeitkonto.verbleibendNachPlan', 'Verbleibend nach Plan')}</span>
                    <span className="text-base font-bold font-mono text-content">{((mehrUeber.verbleibend_nach_plan_min || 0) / 60).toFixed(1).replace('.', ',')} h</span>
                  </div>
                  {Array.isArray(mehrUeber.geplant_tage) && mehrUeber.geplant_tage.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-line space-y-0.5">
                      {mehrUeber.geplant_tage.map((tg) => (
                        <div key={tg.datum} className="flex items-center justify-between text-[11px]">
                          <span className="text-[var(--text-tertiary)]">{fmtDateShort(tg.datum)}</span>
                          <span className="font-mono text-[var(--text-tertiary)]">{(tg.min / 60).toFixed(1).replace('.', ',')} h</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          )}
        </div>
      )}

      {/* Tagesübersicht */}
      {!lightModus && zkData?.tage?.length > 0 && (
        <div className="bg-surface-card border border-line rounded-card overflow-hidden mb-section">
          <div className="px-4 py-3 border-b border-line">
            <h3 className="text-sm font-bold text-content uppercase tracking-wider">{t('portal.zeitkonto.dailyOverview')}</h3>
          </div>
          {/* Desktop: Tabelle */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">{t('portal.zeitkonto.date')}</th>
                  <th className="px-3 py-2 text-right">{t('portal.zeitkonto.soll')}</th>
                  <th className="px-3 py-2 text-right">{t('portal.zeitkonto.netto', 'Netto')}</th>
                  <th className="px-3 py-2 text-right">{t('portal.zeitkonto.delta')}</th>
                  <th className="px-3 py-2 text-right">ZA</th>
                  <th className="px-3 py-2 text-right">{t('portal.zeitkonto.zaKontoAbzug', 'ZA-Konto Abzug')}</th>
                  <th className="px-3 py-2 text-right">{t('portal.zeitkonto.breaks')}</th>
                  <th className="px-3 py-2 text-left">{t('common.status')}</th>
                </tr>
              </thead>
              <tbody>
                {zkData.tage.map(tag => {
                  const isWE = tag.ist_wochenende;
                  const isFT = tag.ist_feiertag;
                  const isAbw = !!tag.abwesenheit;
                  const dimmed = isWE || isFT;
                  const expanded = expandedDay === tag.datum;
                  // #1148 H3 — "offen" = Arbeitstag mit SOLL aber ohne Stempel → orange "?" statt rotem Minus
                  const isOffen = !dimmed && !isAbw && (tag.soll_min || 0) > 0 && (tag.ist_min || 0) === 0 && tag.delta_min < 0;
                  // #1776: an ZA-Tagen zeigt Netto die echte Arbeitszeit, Delta die echte Abweichung.
                  const zaMin = Number(tag.za_min || 0);
                  const nettoMin = zaMin > 0 ? Number(tag.arbeit_min || 0) : Number(tag.ist_min || 0);
                  // #1864 F5: persistiertes Tages-Delta, nicht `arbeit − soll`
                  const zeigDelta = tag.delta_min;

                  return (
                    <tr
                      key={tag.datum}
                      className={`border-b border-line/30 cursor-pointer hover:bg-surface-inset/50 transition-colors ${dimmed ? 'opacity-40' : ''}`}
                      onClick={() => setExpandedDay(expanded ? null : tag.datum)}
                    >
                      <td className="px-4 py-2 font-mono text-xs text-[var(--text-secondary)]">{fmtDateShort(tag.datum)}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{dimmed || isAbw ? '–' : fmtMin(tag.soll_min)}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{dimmed ? '–' : nettoMin > 0 ? fmtMin(nettoMin) : isAbw && zaMin === 0 ? '–' : ''}</td>
                      <td className={`px-3 py-2 text-right font-mono text-xs font-semibold ${dimmed || (isAbw && zaMin === 0) || isOffen ? '' : zeigDelta > 0 ? 'text-emerald-400' : zeigDelta < 0 ? 'text-red-400' : ''}`}>
                        {/* #1845: statt eines „?" die Zahl — der Monatsnachweis druckt
                            fuer denselben Tag den Wert, beide Ansichten stimmen jetzt ueberein. */}
                        {isOffen ? (
                          <span
                            title={t('portal.zeitkonto.tagOffenTooltip', 'Kein Stempel an einem Arbeitstag. Bitte beim Teamleiter melden.')}
                            className="inline-flex items-center justify-center px-1.5 h-5 rounded bg-orange-500/20 text-orange-400 text-[11px] font-bold cursor-help"
                          >
                            {zeigDelta != null ? fmtMin(zeigDelta) : '?'}
                          </span>
                        ) : dimmed || (isAbw && zaMin === 0) ? '–' : zeigDelta != null ? (zeigDelta > 0 ? '+' : '') + fmtMin(zeigDelta) : ''}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{zaMin > 0 ? fmtMin(zaMin) : <span className="text-[var(--text-muted)]">—</span>}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{zaMin > 0 && tag.za_abzug_min > 0 ? fmtMin(-tag.za_abzug_min) : <span className="text-[var(--text-muted)]">—</span>}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-[var(--text-tertiary)]">{dimmed ? '' : tag.pausen_min > 0 ? fmtMin(tag.pausen_min) : ''}</td>
                      <td className="px-3 py-2 text-left">
                        {isFT && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 font-semibold">{t('portal.zeitkonto.holiday')}</span>}
                        {tag.abwesenheit === 'urlaub' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-semibold">{t('portal.zeitkonto.vacation')}</span>}
                        {tag.abwesenheit === 'zeitausgleich' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 font-semibold">ZA</span>}
                        {tag.abwesenheit === 'krank' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 font-semibold">{t('portal.zeitkonto.sick')}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: Card-Liste statt breiter Tabelle (#1593, kein horizontales Scrollen) */}
          <div className="md:hidden divide-y divide-line">
            {zkData.tage.map(tag => {
              // #1776: an ZA-Tagen echte Arbeitszeit + separater ZA-Block.
              const zaMin = Number(tag.za_min || 0);
              const istMin = zaMin > 0 ? Number(tag.arbeit_min || 0) : Number(tag.ist_min || 0);
              const sollMin = tag.soll_min != null ? Number(tag.soll_min) : null;
              const deltaMin = tag.delta_min; // #1864 F5: persistiertes Tages-Delta
              const pausenMin = Number(tag.pausen_min || 0);
              const isWE = tag.ist_wochenende;
              const isFT = tag.ist_feiertag;
              const isAbw = !!tag.abwesenheit;
              const dimmed = isWE || isFT;
              const isOffen = !isWE && !isFT && !isAbw && (sollMin || 0) > 0 && istMin === 0;
              const d = new Date(tag.datum + 'T00:00:00');
              const dayNum = d.getDate();
              const dowLabel = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][d.getDay()];

              let tagLabel, tagColor, tagBg;
              if (isFT) { tagLabel = t('portal.zeitkonto.holiday'); tagColor = 'text-amber-700'; tagBg = 'bg-amber-50'; }
              else if (tag.abwesenheit === 'krank') { tagLabel = t('portal.zeitkonto.sick'); tagColor = 'text-rose-700'; tagBg = 'bg-rose-50'; }
              else if (tag.abwesenheit === 'urlaub') { tagLabel = t('portal.zeitkonto.vacation'); tagColor = 'text-purple-700'; tagBg = 'bg-purple-50'; }
              else if (tag.abwesenheit === 'zeitausgleich') { tagLabel = t('portal.zeitkonto.tagTypeZa'); tagColor = 'text-indigo-700'; tagBg = 'bg-indigo-50'; }
              else if (tag.abwesenheit) { tagLabel = String(tag.abwesenheit); tagColor = 'text-purple-700'; tagBg = 'bg-purple-50'; }
              else if (isWE) { tagLabel = t('portal.zeitkonto.tagTypeWE'); tagColor = 'text-[var(--text-muted,#9CA3AF)]'; tagBg = 'bg-[var(--bg-inset,#F8F9FC)]'; }
              else if (istMin > 0) { tagLabel = t('portal.zeitkonto.tagTypeArbeit'); tagColor = 'text-[#338edc]'; tagBg = 'bg-[#338edc]/10'; }
              else if (isOffen) { tagLabel = t('portal.zeitkonto.tagTypeOffen'); tagColor = 'text-[var(--text-tertiary,#6B7280)]'; tagBg = 'bg-[var(--bg-inset,#F3F4F6)]'; }
              else { tagLabel = t('portal.zeitkonto.frei'); tagColor = 'text-[var(--text-muted,#9CA3AF)]'; tagBg = 'bg-[var(--bg-inset,#F8F9FC)]'; }

              const showDelta = deltaMin != null && !dimmed && (!isAbw || zaMin > 0) && !isOffen;
              return (
                <div key={tag.datum} className={`flex items-center gap-3 px-4 py-2.5 ${dimmed ? 'opacity-50' : ''}`}>
                  <div className="flex flex-col items-center w-8 shrink-0">
                    <span className="text-[10px] text-[var(--text-tertiary)]">{dowLabel}</span>
                    <span className="text-sm font-semibold text-content">{dayNum}</span>
                  </div>
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium shrink-0 ${tagBg} ${tagColor}`}>{tagLabel}</span>
                  <div className="flex-1 min-w-0 text-right">
                    <div className="flex items-baseline justify-end gap-1">
                      {istMin > 0 ? (
                        <span className="text-sm font-semibold font-mono text-content">{fmtMin(istMin)}</span>
                      ) : (!dimmed && !isAbw && (sollMin || 0) === 0) ? (
                        <span className="text-[11px] text-[var(--text-muted,#9CA3AF)]">–</span>
                      ) : null}
                      {sollMin != null && sollMin > 0 && (
                        <span className="text-[10px] font-mono text-[var(--text-tertiary)]">/ {fmtMin(sollMin)}</span>
                      )}
                    </div>
                    {(isOffen || showDelta || pausenMin > 0) && (
                      <div className="flex items-center justify-end gap-2 mt-0.5">
                        {isOffen && (
                          <span title={t('portal.zeitkonto.tagOffenTooltip', 'Kein Stempel an einem Arbeitstag. Bitte beim Teamleiter melden.')} className="inline-flex items-center justify-center px-1 h-4 rounded bg-orange-500/20 text-orange-500 text-[10px] font-bold">{deltaMin != null ? fmtMin(deltaMin) : '?'}</span>
                        )}
                        {showDelta && (
                          <span className={`text-[10px] font-mono ${deltaMin > 0 ? 'text-emerald-600' : deltaMin < 0 ? 'text-red-500' : 'text-[var(--text-tertiary)]'}`}>
                            {deltaMin > 0 ? '+' : ''}{fmtMin(deltaMin)}
                          </span>
                        )}
                        {pausenMin > 0 && (
                          <span className="text-[10px] font-mono text-[var(--text-tertiary)]">⏸ {fmtMin(pausenMin)}</span>
                        )}
                      </div>
                    )}
                    {zaMin > 0 && (
                      <div className="mt-1 text-[10px] text-[var(--text-tertiary)] leading-snug">
                        <div>{t('portal.zeitkonto.zaTag', 'Zeitausgleich')} <span className="font-mono">{fmtMin(zaMin)}</span></div>
                        {tag.za_abzug_min > 0 && (
                          <div>{t('portal.zeitkonto.zaTagAbzug', 'Davon vom ZA-Konto abgezogen')} <span className="font-mono">{fmtMin(-tag.za_abzug_min)}</span></div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 6-Monats-Trend */}
      {!lightModus && chartData.length > 1 && (
        <div className="bg-surface-card border border-line rounded-card p-5">
          <h3 className="text-sm font-bold text-content uppercase tracking-wider mb-4">{t('portal.zeitkonto.trend')}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-line, #E5E7EB)" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-tertiary, #9CA3AF)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--text-tertiary, #9CA3AF)' }} unit="h" />
              <Tooltip
                contentStyle={{ backgroundColor: 'var(--bg-card, #fff)', border: '1px solid var(--border-line, #E5E7EB)', borderRadius: '8px', fontSize: '12px' }}
                formatter={(val) => [`${val}h`]}
              />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              <Bar dataKey="soll" name={t('portal.zeitkonto.soll')} fill="#E5E7EB" radius={[3, 3, 0, 0]} />
              <Bar dataKey="ist" name={t('portal.zeitkonto.ist')} fill="#338edc" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* PDF-Download (#1593, portiert aus MobileZeiten) */}
      <a
        href={`${API}/api/portal/zeitkonto/pdf?monat=${monat}-01`}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-section flex items-center justify-center gap-2 bg-surface-card border border-line rounded-card p-4 text-[#338edc] hover:bg-[#338edc]/5 transition-colors"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
        </svg>
        <span className="text-sm font-medium">{t('portal.zeitkonto.downloadPdf')}</span>
      </a>
    </div>
  );
}
